import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CircleAlert, CircleCheck, Loader2, Mic, ShieldCheck } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { PasswordInput } from '@/components/password-input'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { api, ApiError } from '@/lib/api'
import { ukDate } from '@/lib/quotes'
import type { StaffUser } from '@/lib/session'
import { reportError } from '@/lib/settings'
import { FormField, NativeSelect, SettingsCard, TextInput } from './form-kit'
import { PageTitle } from './pages'

type Provider = 'auto' | 'openrouter' | 'groq' | 'off'
interface AiSettings {
  enabled: boolean
  hasGroqKey: boolean
  hasOpenRouterKey: boolean
  transcribeModel: string
  cleanupProvider: Provider
  cleanupModel: string
  groqCleanupModel: string
  vocabulary: string
  dailyAudioMinutes: number
  verifiedAt: string | null
  lastTestError: string
  version: number
}
interface TestResult {
  groq: { ok: boolean; message: string }
  cleanup: { ok: boolean; provider: string; message: string }
}

const PROVIDERS: { value: Provider; label: string; help: string }[] = [
  {
    value: 'auto',
    label: 'Automatic (recommended)',
    help: 'Uses the OpenRouter free model when an OpenRouter key is saved, and Groq if OpenRouter is busy or has run out for the day. With no OpenRouter key, Groq does it all.',
  },
  {
    value: 'openrouter',
    label: 'OpenRouter free model only',
    help: 'Free models allow 50 clean-ups a day (1,000 a day once $10 of credit has ever been bought). Some free models may keep what is sent to them.',
  },
  {
    value: 'groq',
    label: 'Groq only',
    help: 'Uses the same Groq key: about 1,000 clean-ups a day on the free tier. Groq does not train on your data.',
  },
  {
    value: 'off',
    label: 'Off',
    help: 'Notes are saved exactly as heard, ums and all.',
  },
]

type Form = Omit<
  AiSettings,
  'hasGroqKey' | 'hasOpenRouterKey' | 'verifiedAt' | 'lastTestError' | 'version'
> & {
  groqKey: string
  openRouterKey: string
  removeGroq: boolean
  removeOpenRouter: boolean
}

const toForm = (s: AiSettings): Form => ({
  enabled: s.enabled,
  transcribeModel: s.transcribeModel,
  cleanupProvider: s.cleanupProvider,
  cleanupModel: s.cleanupModel,
  groqCleanupModel: s.groqCleanupModel,
  vocabulary: s.vocabulary,
  dailyAudioMinutes: s.dailyAudioMinutes,
  groqKey: '',
  openRouterKey: '',
  removeGroq: false,
  removeOpenRouter: false,
})

export function DictationSettingsPage(_: { me: StaffUser }) {
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['ai-settings'],
    queryFn: () =>
      api.get<{
        settings: AiSettings
        usage: { todaySeconds: number; todayCount: number }
        encryptionReady?: boolean
      }>('/ai-settings'),
  })
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['ai-settings'] })
    void qc.invalidateQueries({ queryKey: ['dictation-status'] })
  }
  const [f, setF] = useState<Form | null>(null)
  const [result, setResult] = useState<TestResult | null>(null)
  useEffect(() => {
    if (q.data) setF(toForm(q.data.settings))
  }, [q.data])

  const save = useMutation({
    mutationFn: (x: { form: Form; enabledOnly?: boolean }) => {
      const s = q.data!.settings
      const { form } = x
      return api.put<{ settings: AiSettings }>('/ai-settings', {
        enabled: form.enabled,
        transcribeModel: form.transcribeModel,
        cleanupProvider: form.cleanupProvider,
        cleanupModel: form.cleanupModel.trim(),
        groqCleanupModel: form.groqCleanupModel.trim(),
        vocabulary: form.vocabulary,
        dailyAudioMinutes: form.dailyAudioMinutes,
        ...(form.removeGroq ? { groqKey: null } : form.groqKey ? { groqKey: form.groqKey } : {}),
        ...(form.removeOpenRouter
          ? { openRouterKey: null }
          : form.openRouterKey
            ? { openRouterKey: form.openRouterKey }
            : {}),
        expectedVersion: s.version,
      })
    },
    onSuccess: (_d, x) => {
      refresh()
      setResult(null)
      toast.success(
        x.enabledOnly
          ? x.form.enabled
            ? 'Dictation is on'
            : 'Dictation is off'
          : 'Dictation settings saved',
      )
    },
    onError: (e) => reportError(e, refresh),
  })
  const test = useMutation({
    mutationFn: () => api.post<TestResult>('/ai-settings/test'),
    onSuccess: (r) => {
      setResult(r)
      refresh()
    },
    onError: (e) =>
      setResult({
        groq: { ok: false, message: e instanceof ApiError ? e.message : 'The test could not run.' },
        cleanup: { ok: false, provider: '', message: '' },
      }),
  })

  if (q.isError)
    return (
      <p role="alert" className="text-destructive">
        {q.error.message}
      </p>
    )
  if (!q.data || !f) return <Loader2 className="animate-spin" aria-label="Loading" />
  const s = q.data.settings
  const usage = q.data.usage
  const set = (patch: Partial<Form>) => setF({ ...f, ...patch })
  const base = toForm(s)
  const dirty = (Object.keys(base) as (keyof Form)[]).some(
    (k) => k !== 'enabled' && f[k] !== base[k],
  )
  const errors: Record<string, string> = {}
  if (!s.hasGroqKey && !f.groqKey && f.enabled)
    errors.groqKey = 'Add the Groq key before switching dictation on.'
  if (f.groqKey && !/^gsk_\w{20,}$/.test(f.groqKey.trim()))
    errors.groqKey = 'Groq keys start with gsk_. Copy it again from console.groq.com.'
  if (f.openRouterKey && !/^sk-or-/.test(f.openRouterKey.trim()))
    errors.openRouterKey = 'OpenRouter keys start with sk-or-. Copy it again from openrouter.ai.'
  if (
    !Number.isInteger(f.dailyAudioMinutes) ||
    f.dailyAudioMinutes < 1 ||
    f.dailyAudioMinutes > 480
  )
    errors.dailyAudioMinutes = 'Enter a number of minutes from 1 to 480.'
  if (!f.cleanupModel.trim() && ['auto', 'openrouter'].includes(f.cleanupProvider))
    errors.cleanupModel = 'Enter the OpenRouter model, e.g. google/gemma-4-31b-it:free'
  const valid = Object.keys(errors).length === 0
  const provider = PROVIDERS.find((p) => p.value === f.cleanupProvider)!
  const usedMin = Math.round(usage.todaySeconds / 6) / 10

  return (
    <>
      <PageTitle
        title="Dictation"
        description="Talk instead of typing on a walk-round. Speech is written up by Groq Whisper, then tidied to remove the ums and ahs."
      />

      <div
        role="status"
        className={
          s.enabled
            ? 'mb-5 flex items-start gap-3 rounded-xl border border-success/40 bg-success/10 px-4 py-3'
            : 'mb-5 flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/10 px-4 py-3'
        }
      >
        {s.enabled ? (
          <CircleCheck className="mt-0.5 size-5 shrink-0 text-success" aria-hidden />
        ) : (
          <Mic className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden />
        )}
        <div className="text-sm">
          <p className="font-semibold">
            {s.enabled
              ? 'Dictation is on'
              : s.hasGroqKey
                ? 'Ready. Test it, then switch it on below.'
                : 'Dictation is not set up yet'}
          </p>
          <p className="mt-0.5 text-muted-foreground">
            Used today: {usedMin} of {s.dailyAudioMinutes} minutes ({usage.todayCount}{' '}
            {usage.todayCount === 1 ? 'recording' : 'recordings'}).
          </p>
        </div>
      </div>

      {q.data.encryptionReady === false && (
        <p
          role="alert"
          className="mb-5 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm"
        >
          <CircleAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
          The server is missing its encryption key, so API keys cannot be stored safely yet.
        </p>
      )}

      <form
        className="grid gap-5"
        onSubmit={(e) => {
          e.preventDefault()
          if (valid) save.mutate({ form: { ...f, enabled: s.enabled } })
        }}
      >
        <SettingsCard
          title="Speech to text (Groq)"
          description="Get a free key at console.groq.com, under API Keys. The free tier allows about 8 hours of audio a day."
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              label="Groq API key"
              hint={
                s.hasGroqKey && !f.removeGroq
                  ? 'Saved securely. Leave blank to keep it.'
                  : 'Stored encrypted. It is never shown again.'
              }
              error={errors.groqKey}
            >
              {(p) => (
                <PasswordInput
                  {...p}
                  autoComplete="off"
                  value={f.groqKey}
                  placeholder={s.hasGroqKey && !f.removeGroq ? '••••••••••' : 'gsk_…'}
                  onChange={(e) => set({ groqKey: e.target.value.trim(), removeGroq: false })}
                />
              )}
            </FormField>
            <FormField label="Transcription model">
              {(p) => (
                <NativeSelect
                  {...p}
                  value={f.transcribeModel}
                  onChange={(e) => set({ transcribeModel: e.target.value })}
                >
                  <option value="whisper-large-v3-turbo">
                    Whisper Large v3 Turbo (fast, recommended)
                  </option>
                  <option value="whisper-large-v3">
                    Whisper Large v3 (slightly more accurate)
                  </option>
                </NativeSelect>
              )}
            </FormField>
          </div>
          {s.hasGroqKey && (
            <label className="flex min-h-11 items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-(--primary)"
                checked={f.removeGroq}
                onChange={(e) => set({ removeGroq: e.target.checked, groqKey: '' })}
              />
              Remove the saved Groq key
            </label>
          )}
          <FormField
            label="Words to listen for"
            hint="Optional. Client names, products or local places, separated by commas. Helps spelling."
          >
            {(p) => (
              <Textarea
                {...p}
                rows={2}
                maxLength={600}
                value={f.vocabulary}
                onChange={(e) => set({ vocabulary: e.target.value })}
                className="text-base md:text-[15px]"
              />
            )}
          </FormField>
          <FormField
            label="Daily limit (minutes of audio)"
            hint="Dictation stops for the day at this limit. Groq’s free tier allows 480."
            error={errors.dailyAudioMinutes}
            className="sm:max-w-xs"
          >
            {(p) => (
              <TextInput
                {...p}
                inputMode="numeric"
                value={Number.isNaN(f.dailyAudioMinutes) ? '' : String(f.dailyAudioMinutes)}
                onChange={(e) => set({ dailyAudioMinutes: Number(e.target.value || NaN) })}
              />
            )}
          </FormField>
        </SettingsCard>

        <SettingsCard
          title="Tidying up"
          description="An AI model removes ums, repeats and false starts, and fixes punctuation. It keeps every fact, number and room name, and never adds anything. People can always switch back to what was heard."
        >
          <fieldset className="grid gap-2">
            <legend className="mb-1 text-sm font-medium">Who tidies the text</legend>
            {PROVIDERS.map((p) => (
              <label
                key={p.value}
                className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm has-checked:border-primary has-checked:bg-accent/40"
              >
                <input
                  type="radio"
                  name="cleanup"
                  className="size-4 accent-(--primary)"
                  checked={f.cleanupProvider === p.value}
                  onChange={() => set({ cleanupProvider: p.value })}
                />
                <span className="font-medium">{p.label}</span>
              </label>
            ))}
            <p className="text-[13px] text-muted-foreground">{provider.help}</p>
          </fieldset>
          {(f.cleanupProvider === 'auto' || f.cleanupProvider === 'openrouter') && (
            <div className="grid gap-5 sm:grid-cols-2">
              <FormField
                label="OpenRouter API key"
                hint={
                  s.hasOpenRouterKey && !f.removeOpenRouter
                    ? 'Saved securely. Leave blank to keep it.'
                    : 'Optional. From openrouter.ai, under Keys.'
                }
                error={errors.openRouterKey}
              >
                {(p) => (
                  <PasswordInput
                    {...p}
                    autoComplete="off"
                    value={f.openRouterKey}
                    placeholder={
                      s.hasOpenRouterKey && !f.removeOpenRouter ? '••••••••••' : 'sk-or-…'
                    }
                    onChange={(e) =>
                      set({ openRouterKey: e.target.value.trim(), removeOpenRouter: false })
                    }
                  />
                )}
              </FormField>
              <FormField
                label="OpenRouter model"
                hint="A free model ends in :free. Free models change; if one stops working, pick another."
                error={errors.cleanupModel}
              >
                {(p) => (
                  <TextInput
                    {...p}
                    autoCapitalize="none"
                    spellCheck={false}
                    value={f.cleanupModel}
                    onChange={(e) => set({ cleanupModel: e.target.value })}
                  />
                )}
              </FormField>
              {s.hasOpenRouterKey && (
                <label className="flex min-h-11 items-center gap-2 text-sm sm:col-span-2">
                  <input
                    type="checkbox"
                    className="size-4 accent-(--primary)"
                    checked={f.removeOpenRouter}
                    onChange={(e) => set({ removeOpenRouter: e.target.checked, openRouterKey: '' })}
                  />
                  Remove the saved OpenRouter key
                </label>
              )}
            </div>
          )}
          {f.cleanupProvider !== 'off' && f.cleanupProvider !== 'openrouter' && (
            <FormField
              label="Groq tidy-up model"
              hint="Default openai/gpt-oss-20b."
              className="sm:max-w-md"
            >
              {(p) => (
                <TextInput
                  {...p}
                  autoCapitalize="none"
                  spellCheck={false}
                  value={f.groqCleanupModel}
                  onChange={(e) => set({ groqCleanupModel: e.target.value })}
                />
              )}
            </FormField>
          )}
          <p className="flex items-start gap-2 text-[13px] text-muted-foreground">
            <ShieldCheck className="mt-0.5 size-4 shrink-0" aria-hidden />
            Recordings are sent for transcription and not stored by HCD Portal. Avoid dictating
            personal details about people.
          </p>
          <div className="flex justify-end">
            <Button type="submit" className="h-10" disabled={!dirty || !valid || save.isPending}>
              {save.isPending && <Loader2 className="animate-spin" />}
              Save dictation settings
            </Button>
          </div>
        </SettingsCard>
      </form>

      <div className="mt-5 grid gap-5">
        <SettingsCard title="Test and switch on">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              className="h-11 md:h-10"
              disabled={!s.hasGroqKey || dirty || test.isPending}
              onClick={() => test.mutate()}
            >
              {test.isPending && <Loader2 className="animate-spin" />}
              Test the keys
            </Button>
            {dirty && (
              <span className="text-[13px] text-muted-foreground">
                Save your changes before testing.
              </span>
            )}
          </div>
          {result && (
            <ul className="grid gap-2" role="status">
              <TestLine
                label="Speech to text (Groq)"
                ok={result.groq.ok}
                message={result.groq.message}
              />
              {result.cleanup.provider !== '' && (
                <TestLine
                  label={`Tidying up (${result.cleanup.provider})`}
                  ok={result.cleanup.ok}
                  message={result.cleanup.message}
                />
              )}
            </ul>
          )}
          {!result && s.lastTestError && (
            <p className="text-sm text-destructive">Last test: {s.lastTestError}</p>
          )}
          {s.verifiedAt && (
            <p className="text-[13px] text-muted-foreground">
              Last successful test {ukDate(s.verifiedAt, true)}.
            </p>
          )}
          <label className="flex items-start justify-between gap-4 rounded-lg border p-4">
            <span>
              <span className="block text-sm font-medium">Allow dictation</span>
              <span className="block text-[13px] text-muted-foreground">
                {s.hasGroqKey
                  ? 'A microphone button appears on walk-round notes and quote text.'
                  : 'Available once a Groq key is saved.'}
              </span>
            </span>
            <Switch
              checked={s.enabled}
              disabled={!s.hasGroqKey || save.isPending}
              onCheckedChange={(v) =>
                save.mutate({ form: { ...toForm(s), enabled: v }, enabledOnly: true })
              }
              aria-label="Allow dictation"
            />
          </label>
        </SettingsCard>
      </div>
    </>
  )
}

function TestLine({ label, ok, message }: { label: string; ok: boolean; message: string }) {
  return (
    <li
      className={
        ok
          ? 'flex items-start gap-2 text-sm font-medium text-[#0f5c3b] dark:text-success'
          : 'flex items-start gap-2 text-sm font-medium text-destructive'
      }
    >
      {ok ? (
        <CircleCheck className="mt-0.5 size-4 shrink-0" aria-hidden />
      ) : (
        <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
      )}
      <span>
        {label}: {message || (ok ? 'working' : 'not working')}
      </span>
    </li>
  )
}
